/**
 * useDepthViewer — resolves which shop viewer to show for the section currently open.
 *
 *   v1  the morph viewer, driven by cameras.json          — always available
 *   v2  the depth viewer, driven by depth-v2/scan.json    — where depth has been generated
 *   v3  the scene viewer, DepthwithColmap/cloud.json      — where the section has been fused
 *
 * A SECTION is the unit, not a shop. One shop may have depth for two of its six sections, and
 * that is a normal state: this hook re-resolves every time the morph URL changes, which is what
 * `MorphViewerPage` already rebuilds on every section switch.
 *
 * The stored choice is a PREFERENCE, not a pin. It selects the best viewer that exists here and
 * degrades to one that does — pinning a viewer with no data behind it would show a scan that
 * cannot load rather than one that can.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { cloudUrlFrom, depthUrlFrom, hasCloudData, hasDepthData, scanLocationOf } from '@/utils/buildMorphUrl'

export type ViewerKind = 'v1' | 'v2' | 'v3'

const PREFERENCE_KEY = 'dukkan.viewerKind'

/**
 * Probe results, keyed by section base. Walking a shop's sections and coming back should not
 * re-ask what each one has: the answer only changes when someone generates it, which this tab
 * does through `markDepthAvailable` / `markCloudAvailable` below.
 *
 * Deliberately a probe, not the `depthV2` / `depthWithColmap` flags on the shop document. Those
 * are only set by the generation flow, so a section whose data was uploaded by hand would be
 * invisible to them — and the blob either exists or it does not, which is the one answer that
 * is always right. Each manifest is small enough that the probe warms the cache for the viewer
 * about to request it.
 */
const depthProbeCache = new Map<string, boolean>()
const cloudProbeCache = new Map<string, boolean>()

/** Called after a successful generation so the toggle appears without a reload. */
export function markDepthAvailable(base: string): void {
  depthProbeCache.set(base, true)
}

export function markCloudAvailable(base: string): void {
  cloudProbeCache.set(base, true)
}

function readPreference(): ViewerKind | null {
  try {
    const stored = localStorage.getItem(PREFERENCE_KEY)
    return stored === 'v1' || stored === 'v2' || stored === 'v3' ? stored : null
  } catch {
    return null // private mode, or storage disabled
  }
}

export interface DepthViewerState {
  /** The URL to put in the iframe. */
  viewerUrl: string | null
  /** Which viewer that URL is. */
  viewerKind: ViewerKind
  /** Whether this section has depth data at all. */
  depthAvailable: boolean
  /** Whether this section has a fused scene. */
  cloudAvailable: boolean
  /** Every viewer this section can actually open, in ascending order of what it needs. */
  available: ViewerKind[]
  /** True while a probe is in flight, so the UI can avoid flashing a control that vanishes. */
  probing: boolean
  /** Where this section lives, for anything that needs to read its frames. */
  scanLocation: { base: string; sas: string } | null
  /**
   * The shop and section ids AS STORED, parsed out of the scan's own URL.
   *
   * NOT from the app store. `currentSectionId` there is a display index: it produced
   * `sections/2/` for a shop whose sections are actually named `section_2`, so the server
   * looked for frames under a path that does not exist and correctly returned 404. The base
   * URL is the path the viewer already fetches frames from, so it cannot disagree with where
   * the blobs really are.
   */
  scanIds: { shopId: string; sectionId: string } | null
  setViewerKind: (kind: ViewerKind) => void
  /** Call after generating data here, so the toggle appears without a reload. */
  refreshDepth: () => void
}

export function useDepthViewer(morphUrl: string | null): DepthViewerState {
  const [depthAvailable, setDepthAvailable] = useState(false)
  const [cloudAvailable, setCloudAvailable] = useState(false)
  const [probing, setProbing] = useState(false)
  const [preference, setPreference] = useState<ViewerKind | null>(readPreference)

  // Probe the section the morph URL points at. Re-runs on every section change because the
  // morph URL is rebuilt with a new base each time. Both probes go out together: they are two
  // small GETs against the same container, and running them in series would double the window
  // in which the toolbar shows fewer options than the section really has.
  useEffect(() => {
    const location = scanLocationOf(morphUrl)
    if (!location) {
      setDepthAvailable(false)
      setCloudAvailable(false)
      return
    }
    const cachedDepth = depthProbeCache.get(location.base)
    const cachedCloud = cloudProbeCache.get(location.base)
    if (cachedDepth !== undefined && cachedCloud !== undefined) {
      setDepthAvailable(cachedDepth)
      setCloudAvailable(cachedCloud)
      setProbing(false)
      return
    }
    let cancelled = false
    setProbing(true)
    // A stale "available" would show the previous section's viewer for the one just opened.
    setDepthAvailable(false)
    setCloudAvailable(false)
    Promise.all([
      cachedDepth !== undefined ? Promise.resolve(cachedDepth) : hasDepthData(location),
      cachedCloud !== undefined ? Promise.resolve(cachedCloud) : hasCloudData(location),
    ])
      .then(([depth, cloud]) => {
        depthProbeCache.set(location.base, depth)
        cloudProbeCache.set(location.base, cloud)
        if (cancelled) return
        setDepthAvailable(depth)
        setCloudAvailable(cloud)
      })
      .finally(() => { if (!cancelled) setProbing(false) })
    return () => { cancelled = true }
  }, [morphUrl])

  const setViewerKind = useCallback((kind: ViewerKind) => {
    setPreference(kind)
    try { localStorage.setItem(PREFERENCE_KEY, kind) } catch { /* storage disabled */ }
  }, [])

  const location = scanLocationOf(morphUrl)

  /**
   * The last two meaningful path segments of the scan's base URL.
   *
   * Two storage layouts exist and this has to serve both:
   *
   *   scan-frames /<shopId>/sections/<sectionId>/    mobile uploads
   *   scan-results/<datasetId>/<sectionId>/          COLMAP datasets
   *
   * In both, the section id is the LAST segment and its owner is the one before -- skipping a
   * literal "sections". An earlier version required "/sections/" and so returned null for
   * every COLMAP shop, which silently hid the 3D button with no way to tell why. The server
   * takes these ids and finds the layout itself, so no path is ever supplied by the client.
   */
  const scanIds = useMemo(() => {
    if (!location) return null
    let path: string
    try {
      path = new URL(location.base, window.location.origin).pathname
    } catch {
      return null
    }
    const parts = path.split('/').filter(Boolean)
    if (parts.length < 2) return null
    const sectionId = parts[parts.length - 1]
    const owner = parts[parts.length - 2]
    const shopId = owner === 'sections' ? parts[parts.length - 3] : owner
    return shopId && sectionId ? { shopId, sectionId } : null
  }, [location?.base])

  const refreshDepth = useCallback(() => {
    if (!location) return
    markDepthAvailable(location.base)
    setDepthAvailable(true)
  }, [location?.base])

  const available = useMemo<ViewerKind[]>(() => {
    const kinds: ViewerKind[] = ['v1']
    if (depthAvailable) kinds.push('v2')
    if (cloudAvailable) kinds.push('v3')
    return kinds
  }, [depthAvailable, cloudAvailable])

  /**
   * The depth viewer stays the default wherever it exists, and the scene viewer is opt-in.
   *
   * Not because it is worse — it shows more — but because it costs more: sixty textures and a
   * mesh build before it is worth looking at, against one frame for v2. Someone who chooses it
   * keeps it, since the preference survives; someone who has never heard of it is not made to
   * pay for it on a phone.
   */
  const viewerKind: ViewerKind =
    preference && available.includes(preference) ? preference
      : depthAvailable ? 'v2'
      : 'v1'
  const viewerUrl =
    viewerKind === 'v3' ? cloudUrlFrom(morphUrl)
      : viewerKind === 'v2' ? depthUrlFrom(morphUrl)
      : morphUrl

  return {
    // If the swap somehow failed, fall back to the morph URL rather than rendering no iframe.
    viewerUrl: viewerUrl ?? morphUrl,
    viewerKind,
    depthAvailable,
    cloudAvailable,
    available,
    probing,
    scanLocation: location,
    scanIds,
    setViewerKind,
    refreshDepth,
  }
}
